import React, { useMemo, useState } from 'react';
import { SpeakerWithDetails } from '@goldcast/player-config/src/types/speaker-types';
import { useCaptionContainerRef } from '../../ClipPlayer/useCaptionContainerRef';
import PreviewSpeakerVideoCanvas from './PreviewSpeakerVideoCanvas';
import StaticCanvasPreview from './StaticCanvasPreview';
import CanvasPlayerDraggableCaptions from '../CanvasPlayerDraggableCaptions/CanvasPlayerDraggableCaptions';
import { PreviewTemplate } from '../../SideBar/types';
import { getSizeConfig } from '@/libs/sharedAPI/sizeConfig/SizeConfigFactory';
import { useClipsContext } from '@/context/ClipsContext/ClipsContext';
import { getFontSize } from '@/libs/sharedAPI/common';

export default function CanvasPreview({
  template,
  mainPlayer,
  secondaryPlayers,
  currentSpeaker
}: {
  template: PreviewTemplate;
  mainPlayer: React.MutableRefObject<HTMLVideoElement | null>;
  secondaryPlayers: React.RefObject<HTMLVideoElement>[];
  currentSpeaker?: SpeakerWithDetails;
}) {
  const { clipId } = useClipsContext();
  const { clipSize, clipLayout } = useMemo(() => {
    const clipSize = template.size;
    const clipLayout = template.layout;
    return { clipSize, clipLayout };
  }, [template.size, template.layout]);

  const showSpeakerLabels = template.magicLayout?.showSpeakerLabels && template.layout !== 'DEFAULT';

  // config used for video canvas
  const videoSizeConfig = useMemo(
    () => getSizeConfig(clipSize, clipId, clipLayout, 0.5),
    [clipSize, clipId, clipLayout]
  );

  const [captionContainerSizes, setCaptionContainerSizes] = useState({
    width: 0,
    height: 0
  });
  const { captionContainerRef } = useCaptionContainerRef(setCaptionContainerSizes, template);

  const [captionFontSize, captionsHeight] = useMemo(() => {
    const scale = template.caption_styles?.scale || 1;
    const fontSize = getFontSize({ playerHeight: captionContainerSizes.height, scale, clipSize });

    return [
      fontSize,
      ((videoSizeConfig.getVideoBottomPadding() - videoSizeConfig.getVideoSurroundPadding()) /
        videoSizeConfig.getHeight()) *
        captionContainerSizes.height
    ];
  }, [videoSizeConfig, captionContainerSizes, template.caption_styles?.scale, clipSize]);

  const backgroundStyle = useMemo(() => {
    if (!template.magicLayout?.backgroundImage) {
      return {
        backgroundColor: template.magicLayout?.backgroundColor
      };
    }
    return { backgroundImage: `url(${template.magicLayout?.backgroundImage})` };
  }, [template.magicLayout?.backgroundImage, template.magicLayout?.backgroundColor]);

  const visibleSecondaryPlayers = useMemo(
    () =>
      secondaryPlayers
        .filter(player => player.current)
        .sort((a, b) => {
          if (!a.current || !b.current) {
            return 0;
          }
          return a.current.id.localeCompare(b.current.id);
        }),
    [secondaryPlayers]
  );

  return (
    <div
      className="absolute top-0 size-full overflow-hidden rounded-md bg-cover bg-center"
      style={{
        ...backgroundStyle
      }}
      id="player-container-preview"
    >
      <div className="relative grid h-full items-start" data-testid="composite-player" ref={captionContainerRef}>
        {/* Canvas for rendering the video elements on screen */}

        <PreviewSpeakerVideoCanvas
          mainPlayer={mainPlayer}
          clipMetadata={template}
          currentSpeaker={currentSpeaker}
          secondaryPlayers={secondaryPlayers}
        />

        {showSpeakerLabels && (
          <StaticCanvasPreview
            mainPlayer={mainPlayer}
            secondaryPlayers={visibleSecondaryPlayers}
            currentSpeaker={currentSpeaker}
            clipMetadata={template}
          />
        )}

        {!!captionsHeight && (
          <CanvasPlayerDraggableCaptions
            captionFontSize={captionFontSize}
            clipMetadata={template}
            isPreview={true}
            clipId={clipId}
            isDisabled={true}
          />
        )}
      </div>
    </div>
  );
}
